import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type ExternalLink,
  ExternalLinkType,
  useAllExternalLinksQuery,
  useBrandExternalLinksQuery,
  useExternalLinksFilterOptionsQuery,
  useProjectExternalLinksQuery,
} from "@src/__generated__/urql-graphql";
import { type BodyColumnDef, DataTable, Link } from "@src/components/ui-kit";
import { Pagination } from "@src/components/ui-kit/table/pagination";
import { runInAction } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import {
  Fragment,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { DocumentsCtx, ExternalLinkColumns } from "../../StoreDocuments";
import { PopoverRowActions } from "./PopoverRowActions";

export const TableExternalLinks = observer(function TableExternalLinks() {
  const store = useContext(DocumentsCtx);
  const firstItem =
    (store.pagination.currentPage - 1) * store.pagination.perPage;
  const lastItem = firstItem + store.pagination.perPage;
  const projectFilter = store.where.filtersByColumn.get(
    // @ts-expect-error
    ExternalLinkColumns.ProjectId,
  );
  const brandFilter = store.where.filtersByColumn.get(
    // @ts-expect-error
    ExternalLinkColumns.BrandId,
  );
  const typeFilter = store.where.filtersByColumn.get(
    // @ts-expect-error
    ExternalLinkColumns.ModelType,
  );
  const state = useLocalObservable(() => ({
    extLinks: [] as ExternalLink[],
    tableData: [] as ExternalLink[],
  }));

  const [{ data: filterOptions, fetching: fetchingFilterOptions }] =
    useExternalLinksFilterOptionsQuery();

  const [
    { data: projectLinks, fetching: fetchingProjectLinks },
    refetchProjectLinks,
  ] = useProjectExternalLinksQuery({
    pause:
      store.extLinkType !== ExternalLinkType.Project || !store.extLinkModelId,
    variables: {
      id: store.extLinkModelId as string,
    },
  });

  const [
    { data: brandLinks, fetching: fetchingBrandLinks },
    refetchBrandLinks,
  ] = useBrandExternalLinksQuery({
    pause:
      store.extLinkType !== ExternalLinkType.Brand || !store.extLinkModelId,
    variables: {
      id: store.extLinkModelId as string,
    },
  });

  const [{ data: allLinks, fetching: fetchingAllLinks }, refetchAllLinks] =
    useAllExternalLinksQuery({
      pause: store.extLinkType !== "all",
      variables: {
        model_type: undefined,
      },
    });

  useLayoutEffect(() => {
    if (store.extLinkType === "all") return;
    projectFilter?.hide();
    brandFilter?.hide();
    typeFilter?.hide();
  }, [store.extLinkType]);

  useEffect(() => {
    if (!filterOptions) return;

    projectFilter?.setOptions(
      filterOptions.projectsSimpleMap.map(({ id, title }) => ({
        label: title,
        value: id,
      })),
    );

    brandFilter?.setOptions(
      filterOptions.brandsSimpleMap.map(({ id, name }) => ({
        label: name,
        value: id,
      })),
    );
  }, [filterOptions]);

  useEffect(() => {
    store.refetchExtLinks =
      store.extLinkType === ExternalLinkType.Project
        ? refetchProjectLinks
        : store.extLinkType === ExternalLinkType.Brand
          ? refetchBrandLinks
          : store.extLinkType === "all"
            ? refetchAllLinks
            : () => {};
  }, [
    store.extLinkType,
    refetchProjectLinks,
    refetchBrandLinks,
    refetchAllLinks,
  ]);

  useEffect(() => {
    if (store.extLinkType !== ExternalLinkType.Brand) return;
    updateState(brandLinks?.brand?.externalLinks ?? []);
  }, [
    brandLinks?.brand?.externalLinks,
    store.searchTerm,
    brandFilter?.value.length,
    projectFilter?.value.length,
    typeFilter?.value.length,
  ]);

  useEffect(() => {
    if (store.extLinkType !== ExternalLinkType.Project) return;
    updateState(projectLinks?.project?.externalLinks ?? []);
  }, [
    projectLinks?.project?.externalLinks,
    store.searchTerm,
    brandFilter?.value.length,
    projectFilter?.value.length,
    typeFilter?.value.length,
  ]);

  useEffect(() => {
    if (store.extLinkType !== "all") return;
    updateState(allLinks?.externalLinks ?? []);
  }, [
    allLinks?.externalLinks,
    store.searchTerm,
    brandFilter?.value.length,
    projectFilter?.value.length,
    typeFilter?.value.length,
  ]);

  useEffect(() => {
    runInAction(() => {
      state.tableData = [];
    });

    runInAction(() => {
      for (let i = firstItem; i < lastItem; i++) {
        const link = state.extLinks[i];
        if (!link) continue;
        state.tableData.push(link);
      }
      store.pagination.firstItem = firstItem + 1;
      store.pagination.lastItem =
        lastItem < state.extLinks.length ? lastItem : state.extLinks.length;
    });
  }, [state.extLinks, firstItem, lastItem]);

  const columns = useMemo<BodyColumnDef<ExternalLink>[]>(() => {
    const baseColumns: BodyColumnDef<ExternalLink>[] = [
      {
        id: "title",
        meta: { label: t`Title` },
        header: t`Title`,
        cell: ({ row }) => <Text>{row.original.title}</Text>,
      },
      {
        id: "link",
        meta: { label: t`Link` },
        header: t`Link`,
        cell: ({ row }) => {
          const href = row.original.link
            .replaceAll("https://", "")
            .replaceAll("http://", "");

          return (
            <Box maxW="400px" isTruncated>
              <Link href={`https://${href}`} isExternal>
                {row.original.link}
              </Link>
            </Box>
          );
        },
      },
    ];

    if (
      store.extLinkType !== ExternalLinkType.Project &&
      store.extLinkType !== ExternalLinkType.Brand
    ) {
      baseColumns.push({
        id: "relates_to",
        meta: { label: t`Relates to` },
        header: t`Relates to`,
        cell: ({ row }) => {
          const isProject =
            row.original.model_type === ExternalLinkType.Project;
          const relatedOptions = isProject
            ? (projectFilter?.options ?? [])
            : (brandFilter?.options ?? []);

          return (
            <Link
              href={{
                pathname: isProject ? "/projects/detail" : "/brands/detail",
                query: { id: row.original.model_id },
              }}
            >
              <Text>
                {isProject ? <Trans>Project:</Trans> : <Trans>Brand:</Trans>}
                &nbsp;
                {
                  relatedOptions.find(
                    ({ value }) => value === row.original.model_id,
                  )?.label
                }
              </Text>
            </Link>
          );
        },
      });
    }

    baseColumns.push({
      id: "actions",
      meta: { label: t`Actions` },
      header: t`Actions`,
      cell: ({ row }) => <PopoverRowActions link={row.original} />,
    });

    return baseColumns;
  }, [brandFilter?.options, projectFilter?.options, store.extLinkType]);

  const filterLinks = (
    link: ExternalLink,
    type: ExternalLinkType,
    filterValues: string[],
  ) => {
    return link.model_type === type && filterValues.includes(link.model_id);
  };

  const updateState = (data: ExternalLink[]) => {
    if (!data.length) return;
    let links = data;

    if (store.searchTerm.length) {
      const lowerCaseSearch = store.searchTerm.toLowerCase();
      links = data.filter(({ title }) =>
        title.toLowerCase().includes(lowerCaseSearch),
      );
    }

    if (typeFilter?.value.length && typeFilter.value.length > 0) {
      links = links.filter(({ model_type }) =>
        typeFilter.value.includes(model_type),
      );
    }

    if (brandFilter?.value.length && brandFilter?.value.length > 0) {
      links = links.filter((link) =>
        filterLinks(link, ExternalLinkType.Brand, brandFilter.value),
      );
    }

    if (projectFilter?.value.length && projectFilter?.value.length > 0) {
      links = links.filter((link) =>
        filterLinks(link, ExternalLinkType.Project, projectFilter.value),
      );
    }

    runInAction(() => {
      state.extLinks = links;
      store.pagination.setFromPaginatorInfo({
        total: links.length,
        currentPage: 1,
        perPage: store.pagination.perPage,
        lastPage: Math.ceil(links.length / store.pagination.perPage),
      });
    });
  };

  return (
    <DataTable>
      {fetchingProjectLinks ||
      fetchingBrandLinks ||
      fetchingAllLinks ||
      fetchingFilterOptions ? (
        <Center h="300px">
          <Spinner color="teal.500" />
        </Center>
      ) : (
        <Fragment>
          <DataTable.Body
            tableKey="external-links"
            data={state.tableData}
            columns={columns}
          />
          <Pagination store={store.pagination} />
        </Fragment>
      )}
    </DataTable>
  );
});
